/* Dieffematic BetterDocs Translator - frontend v4 (KV-friendly) */
(() => {
  'use strict';
  if (!location.pathname.includes('/a/docs')) return;

  const ENDPOINT = 'https://dieffematic-betterdocs-translate.felice-casiere.workers.dev/translate';
  const LANGUAGE = String(window.Shopify?.locale || document.documentElement.lang || 'it').split('-')[0].toLowerCase();
  if (!['en', 'fr', 'de', 'es', 'pt'].includes(LANGUAGE)) return;

  // Deliberately restrict to BetterDocs, not the entire Shopify page.
  const REGIONS = '.sidebar__link, .bdl2-archive-subcategory-block, #betterdocs-faq-section, .faq__content, .faq_content, [class*="bdl2-"], [class*="betterdocs-"]';
  const SKIP = 'script,style,noscript,textarea,code,pre,svg,math,[translate="no"],[contenteditable="true"]';
  const CACHE_KEY = `dieffematic-bd-v4-${LANGUAGE}`;
  const OLD_CACHE_KEY = `dieffematic-bd-v3-${LANGUAGE}`;
  const MAX_PARALLEL = 4;
  const MAX_TEXTS_PER_REQUEST = 12;
  const MAX_BATCH_CHARS = 3000;
  const MAX_CACHE_ITEMS = 6500;

  const translations = new Map();
  const pending = new Set();
  const inFlight = new Set();
  const failed = new Set();
  let active = 0;
  let scanTimer = null;
  let saveTimer = null;
  let initialScanDone = false;

  for (const key of [OLD_CACHE_KEY, CACHE_KEY]) {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(saved)) for (const item of saved) {
        if (Array.isArray(item) && typeof item[0] === 'string' && typeof item[1] === 'string') {
          translations.set(item[0], item[1]);
        }
      }
    } catch (_) {}
  }

  function saveSoon() {
    if (saveTimer) return;
    saveTimer = setTimeout(() => {
      saveTimer = null;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify([...translations].slice(-MAX_CACHE_ITEMS)));
      } catch (_) {}
    }, 500);
  }

  function eligible(node) {
    if (node.nodeType !== 3 || !node.parentElement) return false;
    if (!node.parentElement.closest(REGIONS) || node.parentElement.closest(SKIP)) return false;
    const value = node.nodeValue || '';
    return value.trim().length > 1 && value.length <= 800 && /[\p{L}]/u.test(value);
  }

  function apply(node, translated) {
    if (!node.isConnected || typeof translated !== 'string' || !translated.trim()) return;
    const original = node.__bdOriginal;
    if (typeof original !== 'string') return;
    if (node.nodeValue !== original && node.nodeValue !== node.__bdRendered) return;
    const left = original.match(/^\s*/)?.[0] || '';
    const right = original.match(/\s*$/)?.[0] || '';
    const result = left + translated + right;
    node.__bdRendered = result;
    if (node.nodeValue !== result) node.nodeValue = result;
  }

  function collectFrom(root) {
    if (!root || !root.isConnected) return;
    if (root.closest?.('header,footer,nav.site-nav')) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!eligible(node)) continue;
      // When BetterDocs reuses an existing text node, treat the new value as Italian source.
      if (node.__bdOriginal === undefined ||
          (node.nodeValue !== node.__bdOriginal && node.nodeValue !== node.__bdRendered)) {
        node.__bdOriginal = node.nodeValue;
        node.__bdRendered = undefined;
      }
      const text = node.__bdOriginal.trim();
      if (translations.has(text)) apply(node, translations.get(text));
      else if (!failed.has(text)) pending.add(node);
    }
  }

  function scan() {
    // Exclude nested roots to avoid repeatedly traversing large documentation trees.
    const roots = [...document.querySelectorAll(REGIONS)]
      .filter(el => !el.parentElement?.closest(REGIONS));
    for (const root of roots) collectFrom(root);
    if (!initialScanDone) initialScanDone = true;
    pump();
  }

  function takeBatch() {
    const grouped = new Map();
    let total = 0;
    const nodes = [...pending].sort((a, b) =>
      Number(!a.parentElement?.getClientRects().length) - Number(!b.parentElement?.getClientRects().length)
    );
    for (const node of nodes) {
      if (!node.isConnected || !eligible(node)) { pending.delete(node); continue; }
      const text = node.__bdOriginal?.trim();
      if (!text) { pending.delete(node); continue; }
      if (translations.has(text)) { apply(node, translations.get(text)); pending.delete(node); continue; }
      if (failed.has(text)) { pending.delete(node); continue; }
      if (inFlight.has(text)) { pending.delete(node); continue; }
      if (!grouped.has(text)) {
        if (grouped.size >= MAX_TEXTS_PER_REQUEST || total + text.length > MAX_BATCH_CHARS) continue;
        grouped.set(text, []);
        total += text.length;
        inFlight.add(text);
      }
      grouped.get(text).push(node);
      pending.delete(node);
    }
    return grouped;
  }

  function pump() {
    while (active < MAX_PARALLEL && pending.size) {
      const grouped = takeBatch();
      if (!grouped.size) break;
      const texts = [...grouped.keys()];
      active++;
      (async () => {
        try {
          const response = await fetch(ENDPOINT, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({lang: LANGUAGE, texts})
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          if (!Array.isArray(data.translations) || data.translations.length !== texts.length) {
            throw new Error('Invalid translation payload');
          }
          texts.forEach((source, i) => {
            const translated = data.translations[i];
            if (typeof translated !== 'string' || !translated.trim()) return;
            translations.set(source, translated);
            grouped.get(source).forEach(node => apply(node, translated));
          });
          saveSoon();
        } catch (error) {
          texts.forEach(source => failed.add(source));
          console.warn('[BetterDocs translator v4]', error);
        } finally {
          texts.forEach(source => inFlight.delete(source));
          active--;
          scheduleScan(50);
          pump();
        }
      })();
    }
  }

  function scheduleScan(delay = 80) {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, delay);
  }

  const observer = new MutationObserver(records => {
    // Ignore our own text replacements; watch new nodes and external text changes.
    if (records.some(r =>
      (r.type === 'childList' && r.addedNodes.length) ||
      (r.type === 'characterData' && r.target.nodeValue !== r.target.__bdRendered)
    )) scheduleScan(75);
  });
  observer.observe(document.documentElement, {childList: true, characterData: true, subtree: true});
  scan();
  window.addEventListener('popstate', () => scheduleScan(30));
  console.info(`[BetterDocs translator] v4 active (${LANGUAGE}), cached: ${translations.size}`);
})();
